/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createAsyncThunk } from '@reduxjs/toolkit';

import { sendTransactions } from '@ibm-watson/shared/modules/actions/api/transactions';
import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import {
  getActionSkillId,
  getAssistantId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { ApiStatus } from '../../../../../ts';
import { CreateAsyncThunk, DataTypesSlice } from '../../../../../ts/state';
import { selectIsAccessUpdatesEnabled } from '../../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../../ia/store/instance/selectors';
import { fetchEntities } from '../../entities/actions';
import { listVariables } from '../../variables/actions';
import { entityAdapter } from '../adapter';

export const deleteDataType = createAsyncThunk<
  string,
  {
    dataTypeId: string;
    entityId: string;
  },
  CreateAsyncThunk
>('data-types/delete', async (params, { dispatch, getState }) => {
  try {
    const { dataTypeId, entityId } = params;
    const skillId = getActionSkillId(getState());

    const operations = [];
    operations.push({
      operation: 'delete',
      target_reference: dataTypeId,
      type: 'data_type'
    });
    operations.push({
      operation: 'delete',
      target_reference: entityId,
      type: 'entity'
    });

    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const assistantId = getAssistantId(getState());

    await sendTransactions(
      skillId,
      { operations },
      useNewIAUpdatedAccess,
      assistantId
    );
    await dispatch(listVariables(skillId));
    await dispatch(fetchEntities(skillId, {}, true, true));
    return dataTypeId;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

const reducer = {
  [deleteDataType.pending.toString()]: (state: DataTypesSlice) => {
    state.deleteStatus = ApiStatus.LOADING;
    state.deleteError = null;
  },
  [deleteDataType.fulfilled.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.deleteStatus = ApiStatus.SUCCEEDED;
    state.deleteError = null;
    entityAdapter.removeOne(state, action.payload);
  },
  [deleteDataType.rejected.toString()]: (
    state: DataTypesSlice,
    action: AnyAction
  ) => {
    state.deleteStatus = ApiStatus.ERROR;
    state.deleteError = action.error;
  }
};

export default reducer;
